import {
    COLLECTION_TYPE_SYMBOL,
    type Collection,
    type CollectionEntry,
    type CollectionIterateFn,
} from '../collection';
import { equals } from '../equals';
import { freezeCollection, hashCollection } from '../hash';

const RECORD_TYPE_SYMBOL = Symbol('map');

/**
 * A record is similar to a JS object, but enforces a specific set of allowed string keys, and has default values.
 */
export function ImmutableRecord<Properties extends {}>(defaults: Properties) {
    const keys = Object.keys(defaults) as Array<keyof Properties>;

    class Record implements Collection<keyof Properties, Properties[keyof Properties]> {
        private _values: Partial<Properties>;

        constructor(
            properties: Partial<Properties>,
            constructorTypeCheck: typeof RECORD_TYPE_SYMBOL
        ) {
            if (constructorTypeCheck !== RECORD_TYPE_SYMBOL) {
                throw new Error(
                    `${this.constructor.name} constructor is private, use ${this.constructor.name}.create() instead`
                );
            }

            this._values = properties;
        }

        /**
         * Create a new unique record instance.
         */
        public static createRecord<T extends typeof Record>(
            this: T,
            properties: Partial<Properties> = {}
        ): InstanceType<T> {
            const instance = new this(properties, RECORD_TYPE_SYMBOL) as InstanceType<T>;
            return freezeCollection(instance);
        }

        public getByIndex(
            index: number
        ): CollectionEntry<keyof Properties, Properties[keyof Properties]> | undefined {
            if (index < 0 || index >= keys.length) {
                return undefined;
            }

            // @ts-ignore
            return this.getByKey(keys[index]);
        }

        public getByKey(
            key: keyof Properties
        ): CollectionEntry<keyof Properties, Properties[keyof Properties]> | undefined {
            if (!keys.includes(key)) {
                return undefined;
            }

            return [key, this.get(key)];
        }

        /**
         * Returns the number of elements stored in the record.
         */
        public get size(): number {
            return keys.length;
        }

        /**
         * Return the value associated with a key.
         * object.key can be used as a shorthand for object.get('key').
         */
        public get<Key extends keyof Properties>(key: Key): Properties[Key] {
            if (!(key in this._values)) {
                return defaults[key];
            }

            // @ts-ignore
            return this._values[key];
        }

        /**
         * Update the value associated with a key.
         */
        public set<Key extends keyof Properties>(key: Key, value: Properties[Key]): this {
            if (equals(this.get(key), value)) {
                return this;
            }

            // @ts-ignore
            return this.merge({ [key]: value });
        }

        /**
         * Reset a key to its initial value.
         */
        public delete<Key extends keyof Properties>(key: Key): this {
            const { [key]: _, ...rest } = this._values;
            // @ts-ignore
            return freezeCollection(makeRecord(this, rest));
        }

        /**
         * Update multiples values at once.
         */
        public merge(properties: Partial<Properties>): this {
            return freezeCollection(makeRecord(this, { ...this._values, ...properties }));
        }

        /**
         * Shallowly converts this record to an Object.
         */
        public toObject(): Properties {
            const result: Properties = {} as Properties;
            keys.forEach((key) => {
                // @ts-ignore
                result[key] = this.get(key);
            });
            return result;
        }

        /**
         * Super-set of the collection iterate.
         */
        public iterate(
            fn: CollectionIterateFn<keyof Properties, Properties[keyof Properties]>
        ): void {
            // @ts-ignore
            keys.forEach((key) => fn(this.get(key), key));
        }

        public equals(other: any): boolean {
            if (this === other) {
                return true;
            }

            if (this.constructor !== other.constructor || this.size !== other.size) {
                return false;
            }

            if (hashCollection(this) !== hashCollection(other)) {
                return false;
            }

            for (const key of keys) {
                if (!equals(this.get(key), other.get(key))) {
                    return false;
                }
            }

            return true;
        }

        public toKeysArray(): Array<keyof Properties> {
            return [...keys];
        }

        public toValuesArray(): Array<Properties[keyof Properties]> {
            // @ts-ignore
            return keys.map((key) => this.get(key));
        }

        public toEntriesArray(): Array<[keyof Properties, Properties[keyof Properties]]> {
            // @ts-ignore
            return keys.map((key) => [key, this.get(key)]);
        }

        public hashCode(): number {
            return hashCollection(this);
        }
    }

    for (const key of keys) {
        Object.defineProperty(Record.prototype, key, {
            enumerable: true,
            get() {
                return this.get(key);
            },
            set() {
                throw new Error('Cannot set a property on a Record');
            },
        });
    }

    /**
     * For fast type-checking.
     * We store it on the prototype so that it's not enumerable and will be copied over by makeRecord.
     */
    // @ts-ignore
    Record.prototype[COLLECTION_TYPE_SYMBOL] = RECORD_TYPE_SYMBOL;

    return Record;
}

/**
 * Fast constructor for new record entries as "new X" is slower.
 */
function makeRecord(likeRecord: any, values: object) {
    const record = Object.create(Object.getPrototypeOf(likeRecord));
    record._values = values;
    return record;
}
